import {
  useGetAllSeasonsCategories,
  useGetButtons,
  useGetTopMenu1Categories,
  useGetTopMenus,
} from '@hts/features/libs';
import { useLazyGetDraftQuery } from '@hts/shared/api';
import { ENotAuthNavigation, EPageSubmenuType } from '@hts/shared/enums';
import { mainActions, useAppDispatch, useIsMobile } from '@hts/shared/libs';
import { CardService } from '@hts/shared/services';
import {
  ICategoryItemInput,
  IGetStaticElementsResponse,
  IMappedSubmenuCategory,
  IPageDepartmentSectionEntry,
  ItemType,
} from '@hts/shared/types';
import { HtsButton, LinkElement } from '@hts/shared/ui';
import { shouldRenderOnCurrentView } from '@hts/shared/utils';
import * as Sentry from '@sentry/react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../../styles/Header/Header.module.scss';
import { NotificationBar } from '../NotificationBar';
import { SearchForm } from '../SearchForm';
import { AllSeasonsDropdown } from './AllSeasonsDropdown';
import { AllSimchasDropdown } from './AllSimchasDropdown';
import { MobileHeader } from './Mobile';
import { SubmenuItem } from './SubmenuItem';
import { TopSection } from './TopSection';

type ButtonType = ItemType<IGetStaticElementsResponse['buttons']>;

interface HeaderProps {
  data?: IGetStaticElementsResponse;
  pageDepartment: IPageDepartmentSectionEntry | null;
}

export const Header: React.FC<HeaderProps> = memo(({ data, pageDepartment }) => {
  const dispatch = useAppDispatch();

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [getDraftRequest] = useLazyGetDraftQuery();

  const cardService = useMemo(() => new CardService(), []);

  const { topMenu1, topMenu2 } = useGetTopMenus(data?.menus);

  const topMenus2Categories = topMenu2?.submenus?.[0]?.categories ?? [];

  const allSeasonsCategories = useGetAllSeasonsCategories(data?.menus);
  const allSimchasCategories = useGetTopMenu1Categories(
    topMenu1?.submenus,
    EPageSubmenuType.ALL_SIMCHAS
  );

  const buttons = useGetButtons(data?.buttons);

  const notificationBars = useMemo(
    () =>
      data?.notificationBars?.filter(({ shouldShowOnDesktop, shouldShowOnMobile }) =>
        shouldRenderOnCurrentView(isMobile, shouldShowOnDesktop, shouldShowOnMobile)
      ) ?? [],
    [isMobile, data?.notificationBars]
  );

  const onCloseSearchForm = useCallback(
    (redirectTo?: string) => {
      if (redirectTo) {
        navigate(redirectTo);
      }
    },
    [navigate]
  );

  const renderTop2MenuSubmenu = useCallback(
    (submenu: IMappedSubmenuCategory, index: number, arr: IMappedSubmenuCategory[]) => (
      <SubmenuItem key={`${submenu.id}-${index}`} item={submenu} />
    ),
    []
  );

  const renderButton = useCallback(
    ({ id, buttonText, buttonColor, link, title }: ButtonType, index: number) => (
      <Link key={`${id}-${index}`} to={link}>
        <HtsButton label={buttonText} style={{ background: buttonColor }} />
      </Link>
    ),
    []
  );

  const getDraft = useCallback(async () => {
    const draftParamId = searchParams.get('draft');

    if (!draftParamId) return;

    const { data } = await getDraftRequest(draftParamId);

    if (data) {
      dispatch(mainActions.setDraftCard(data));
      navigate(`${ENotAuthNavigation.CARD}/${data.card_id}`);
    }
  }, [searchParams, getDraftRequest]);

  const getMenu = useCallback(async () => {
    try {
      const res = await cardService.getMenu();

      let categories: ICategoryItemInput[] = [];

      res.forEach((menu) => (categories = [...categories, ...menu.categories]));

      dispatch(mainActions.setAllCategories(categories));

      // await Promise.all(
      //   res.map(async (item, i) =>
      //     item.categories.map(async (cat, index) => {
      //       if (cat.iconUrl.length && cat.iconUrl.indexOf('.svg')) {
      //         try {
      //           const svg = await fetch(cat.iconUrl);
      //           const svgIcon = await svg.text();

      //           newRes[i].categories[index].svgIcon = svgIcon;
      //         } catch (err) {
      //           console.log(err);
      //         }
      //       }
      //     })
      //   )
      // );
    } catch (err) {
      console.log('Menu error', err);
    }

    try {
      const mainMenuRes = await cardService.getMainMenu();

      dispatch(mainActions.setCategories(mainMenuRes));
    } catch (err) {
      Sentry.captureException(err);
      console.log('Header: get main menu error:', err);
    }
  }, []);

  useEffect(() => {
    getDraft();
  }, [getDraft]);

  useEffect(() => {
    getMenu();
  }, []);

  if (isMobile) {
    return <MobileHeader notificationBars={notificationBars} />;
  }

  return (
    <header className={styles.container}>
      <TopSection pageDepartment={pageDepartment} />

      <div className={styles.centerContainer}>
        <div className={styles.contentContainer}>
          <div className={styles.logoContainer}>
            <Link to="/">
              <img
                className={styles.logoImage}
                src="/assets/images/ihopetoseeyou-logo.png"
                alt="Ihopetoseeyou.com"
              />
            </Link>
          </div>

          <div className={styles.rightContainer}>
            <div className={styles.menusContainer}>
              <div className={styles.dropdownsContainer}>
                <AllSimchasDropdown label="All Simchas" categories={allSimchasCategories} />

                <AllSeasonsDropdown label="All Seasons" categories={allSeasonsCategories} />
              </div>

              <SearchForm onClose={onCloseSearchForm} />

              {Boolean(buttons.length) && (
                <div className={styles.buttonsContainer}>{buttons.map(renderButton)}</div>
              )}

              <div className={styles.linksContainer}>
                <LinkElement type="help" />
                <LinkElement type="faq" />
                {/* <LinkElement type="signInUp" /> */}
              </div>
            </div>

            {Boolean(topMenus2Categories.length) && (
              <ul className={styles.categoriesContainer}>
                {topMenus2Categories.map(renderTop2MenuSubmenu)}
              </ul>
            )}
          </div>
        </div>
      </div>

      {notificationBars.length > 0 && <NotificationBar items={notificationBars} />}
    </header>
  );
});
